<template>
    <div class="w-full bg-card-default" id="churches">
        <div class="mx-auto pt-12 lg:pt-24" style="max-width: 1400px;">
            <div class="pb-5 lg:pb-5 mt-10 md:mt-16 text-left px-12 lg:px-24">
                <h2 class="font-written text-4xl">Unsere Kirchen</h2>
                <div class="flex items-center gap-3 mt-1">
                    <img src="@/assets/icons/click.png" class="h-5 opacity-50" alt="Klicke um mehr zu erfahren">
                    <p class="text-sm text-gray-500 mt-2"><span class="font-bold">Klicken,</span> um mehr zu erfahren.
                    </p>
                </div>
            </div>
            <div class="relative flex items-center justify-between gap-2">
                <button type="button"
                    :class="`z-[35] w-12 lg:w-24 h-72 pb-10 shrink-0 group focus:outline-none ${scrollToRight === 0 ? 'opacity-0 cursor-default' : 'opacity-100'}`"
                    @click="toLeft" aria-label="Kirchenübersicht vorwärts navigieren">
                    <span
                        class="inline-flex items-center justify-center w-10 h-10 -ml-2 lg:rounded-full rounded-r-full py-10 lg:py-0 bg-black/30 group-hover:bg-black/50">
                        <svg class="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180" aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M5 1 1 5l4 4" />
                        </svg>
                        <span class="sr-only">Vorheriges</span>
                    </span>
                </button>

                <div class="overflow-hidden relative select-none flex-auto flex" ref="scrollable">
                    <div class="flex duration-500 ease-in-out" :style="`transform: translateX(${-scrollToRight}px)`"
                        ref="content">
                        <template v-for="i in churches" :key="i">
                            <div class="no-scroll w-[99%] sm:w-[48%] md:w-[32%] mx-[0.5%] sm:mx-[1%] md:mx-[0.68%] 
                            overflow-hidden pb-10 shrink-0 flex flex-col relative group cursor-pointer"
                                @click="() => { if (!dragging && i.path) $router.push(i.path) }">

                                <div class="aspect-square overflow-hidden w-full rounded-2xl" style="
                                        mask-image: url('/assets/masks/selection-item-mask.svg');
                                        mask-size: 125%;
                                        mask-position: right top;
                                        mask-repeat: no-repeat;">

                                    <img :src="i.image" class="h-full w-full object-cover group-hover:scale-105 duration-1000" draggable="false" :alt="`Kirche ${i.name}`">
                                </div>


                                <p class="group-hover:bg-primary-default group-hover:text-white duration-200 
                                py-1 px-3 absolute bottom-0 w-min rounded-2xl overflow-hidden text-left">
                                    {{ i.name }}
                                </p>

                                <div class="group-hover:bg-primary-default duration-200 absolute top-0 right-0 h-[25%] aspect-square bg-card-dark rounded-lg flex">
                                    <div class="m-auto" v-if="i.day && !i.day?.includes('Invalid')">
                                        <p class="text-xl font-normal text-white">{{ i.day || 1 }}</p>
                                        <p class="text-sm text-white/50 -mt-1 font-light">{{ i.month || "Jan." }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <button type="button"
                    :class="`z-[35] w-12 lg:w-24 h-72 pb-10 shrink-0 cursor-pointer group focus:outline-none ${scrollToRight === $refs.content?.scrollWidth - containerWidth ? 'opacity-0 cursor-default' : 'opacity-100'}`"
                    @click="toRight" aria-label="Kirchenübersicht zurück navigieren">
                    <span
                        class="inline-flex items-center justify-center w-10 h-10 -mr-2 lg:rounded-full rounded-l-full py-10 lg:py-0 bg-black/30 group-hover:bg-black/50">
                        <svg class="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180" aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="m1 9 4-4-4-4" />
                        </svg>
                        <span class="sr-only">Nächstes</span>
                    </span>
                </button>
            </div>
        </div>
    </div>
    <div class="bricks bg-[length:900px] md:bg-[length:1300px]"></div>
</template>

<script>
import moment from 'moment';
import "moment/locale/de";

export default {
    name: 'Selection',
    data() {
        return {
            containerWidth: 0,
            scrollToRight: 0,
            dragging: false,
            churches: [
                {
                    name: 'Rastenberg',
                    image: '/assets/selection/rastenberg.webp',
                    path: '/kirchen/rastenberg'
                },
                {
                    name: 'Roldisleben',
                    image: '/assets/selection/roldisleben.webp',
                    path: '/kirchen/roldisleben'
                },
                {
                    name: 'Olbersleben',
                    image: '/assets/selection/olbersleben.webp',
                    path: '/kirchen/olbersleben'
                },
                {
                    name: 'Guthmannshausen',
                    image: '/assets/selection/guthmannshausen.webp',
                    path: '/kirchen/guthmannshausen'
                },
                {
                    name: 'Eßleben',
                    image: '/assets/selection/essleben.webp',
                    path: '/kirchen/essleben'
                },
                {
                    name: 'Großneuhausen',
                    image: '/assets/selection/grossneuhausen.webp',
                    path: '/kirchen/großneuhausen'
                },
                {
                    name: 'Kleinneuhausen',
                    image: '/assets/selection/kleinneuhausen.webp',
                    path: '/kirchen/kleinneuhausen'
                },
                {
                    name: 'Teutleben',
                    image: '/assets/selection/teutleben.webp',
                    path: '/kirchen/teutleben'
                },
                {
                    name: 'Mannstedt',
                    image: '/assets/selection/mannstedt.webp',
                    path: '/kirchen/mannstedt'
                }
            ]
        }
    },
    mounted() {
        this.setContainerWidth();
        window.addEventListener('resize', this.setContainerWidth);
    },
    unmounted() {
        window.removeEventListener('resize', this.setContainerWidth);
    },
    created() {
        this.getDates();
    },
    methods: {
        setContainerWidth() {
            this.containerWidth = this.$refs.scrollable.clientWidth;
            this.scrollToRight = 0;
        },
        toRight() {
            this.scrollToRight += this.containerWidth;
            if (this.scrollToRight >= this.$refs.content.scrollWidth - this.containerWidth) {
                this.scrollToRight = this.$refs.content.scrollWidth - this.containerWidth;
            }
        },
        toLeft() {
            this.scrollToRight -= this.containerWidth;
            if (this.scrollToRight <= 0) {
                this.scrollToRight = 0;
            }
        },
        async getDates() {
            let data = await this.$userStore.getData();
            this.churches.map(ch => {
                let date = data.find(f => f.location.includes(ch.name))?.date;
                let momentDate = moment(date, "DD.MM.YYYY");
                ch.day = momentDate.format('D');
                ch.month = momentDate.format('MMM');
                return ch;
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.bricks {
    background-image: url('@/assets/bricks.svg');
    background-position: center top;
    background-repeat: repeat-x;
    width: 100%;
    height: 250px;
}
</style>